import React, { useState, useEffect } from 'react';
import { ShoppingCart, Zap, MapPin, AlertCircle, Shield, Activity, ChevronUp, Landmark, Map as MapIcon, Navigation, Loader2, X, Coins, Fuel } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useMapStore } from '../../store/useMapStore';
import ResultCard from '../ResultCard';
import { HealthSummaryCard } from '../../features/health/HealthSummaryCard';
import { useGisWorker } from '../../hooks/useGisWorker';
import { getOfficeTypeLabelKey } from '../../utils/postal';
import { ConstituencyDetailCard } from '../../features/election/components/ConstituencyDetailCard';
import { RegistrationCard } from '../../features/registration/components/RegistrationCard';
import { LandParcelCard } from '../../features/land_parcel/components/LandParcelCard';
import { RadioStationCard } from '../../features/radio/components/RadioStationCard';
import { SHOW_ELECTION_RESULTS, ENABLE_LAND_PARCEL, ENABLE_POS_DASHBOARD } from '../../config/features';
import { usePosData } from '../../hooks/usePosData';
import { usePoliceContact } from '../../hooks/usePoliceContacts';

import { useTranslation } from '../../i18n/translations';
import { translateDistrict } from '../../i18n/districts';
import type { Position, HealthFilters, GisFeature } from '../../types/gis';
import {
  getBrandLabel,
  getBrandColor,
  getFuelGroupLabel,
  resolveFuelGroups,
  formatAmenity,
  formatHours,
  formatStationTitle
} from '../../constants/fuel';
import { getFeatureShareUrl, getRawShareUrl } from '../../utils/shareUtils';
import type { ServiceLayer } from '../../types/gis';

// Split a contact field that may hold several numbers ("9498101435; 9498106911")
// into individual entries, each with its own tel: dial string.
const splitPhoneNumbers = (value?: unknown): { display: string; dial: string }[] => {
  if (value === null || value === undefined || value === '') return [];
  const str = value.toString();
  return str
    .split(/[;,]/)
    .map(s => s.trim())
    .filter(s => /\d/.test(s))
    .map(s => ({ display: s, dial: s.replace(/[^0-9+]/g, '') }));
};

const ResultContainer: React.FC = () => {
  const activeLayer = useMapStore(state => state.activeLayer);
  const language = useMapStore(state => state.language);
  const isResultMinimized = useMapStore(state => state.isResultMinimized);
  const setIsResultMinimized = useMapStore(state => state.setIsResultMinimized);
  const [isTempleModalOpen, setIsTempleModalOpen] = useState(false);
  const { getPosData } = usePosData();

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const theme = useMapStore(state => state.theme);
  const selectedPdsShop = useMapStore(state => state.selectedPdsShop);
  const setSelectedPdsShop = useMapStore(state => state.setSelectedPdsShop);
  const searchResult = useMapStore(state => state.searchResult);
  const jurisdictionDetails = useMapStore(state => state.jurisdictionDetails);
  const jurisdictionGeometry = useMapStore(state => state.jurisdictionGeometry);
  const setJurisdictionDetails = useMapStore(state => state.setJurisdictionDetails);
  const policeResolution = useMapStore(state => state.policeResolution);
  const setPoliceResolution = useMapStore(state => state.setPoliceResolution);
  // SHO contact enrichment for the police card (district + PS code keyed).
  const policeContact = usePoliceContact(
    policeResolution?.boundary?.properties?.district_n,
    policeResolution?.station?.properties?.ps_code || policeResolution?.boundary?.properties?.police_s_1,
  );
  const setReportModal = useMapStore(state => state.setReportModal);
  const { t } = useTranslation();
  const noDataFound = useMapStore(state => state.noDataFound);
  const lastClickedPoint = useMapStore(state => state.lastClickedPoint);
  const setNoDataFound = useMapStore(state => state.setNoDataFound);
  const clearSearch = useMapStore(state => state.clearSearch);
  const selectedPostalOffices = useMapStore(state => state.selectedPostalOffices);
  const setSelectedPostalOffice = useMapStore(state => state.setSelectedPostalOffice);
  const selectedPostalOffice = useMapStore(state => state.selectedPostalOffice);
  const healthSummary = useMapStore(state => state.healthSummary);
  const selectedHealthFacility = useMapStore(state => state.selectedHealthFacility);
  const setSelectedHealthFacility = useMapStore(state => state.setSelectedHealthFacility);
  const healthScope = useMapStore(state => state.healthScope);
  const activeDistrict = useMapStore(state => state.activeDistrict);
  const selectedLocalBodyV2 = useMapStore(state => state.selectedLocalBodyV2);
  const setSelectedLocalBodyV2 = useMapStore(state => state.setSelectedLocalBodyV2);
  const selectedVeterinaryFacility = useMapStore(state => state.selectedVeterinaryFacility);
  const setSelectedVeterinaryFacility = useMapStore(state => state.setSelectedVeterinaryFacility);
  const isV2Loading = useMapStore(state => state.isV2Loading);
  const selectedRegistrationOffice = useMapStore(state => state.selectedRegistrationOffice);
  const selectedPoliceStation = useMapStore(state => state.selectedPoliceStation);
  const selectedLandParcel = useMapStore(state => state.selectedLandParcel);
  const isLandParcelLoading = useMapStore(state => state.isLandParcelLoading);
  const landParcelError = useMapStore(state => state.landParcelError);
  const setLandParcelError = useMapStore(state => state.setLandParcelError);
  const setSearchQuery = useMapStore(state => state.setSearchQuery);
  const setUserTyping = useMapStore(state => state.setUserTyping);
  const selectedTemple = useMapStore(state => state.selectedTemple);
  const setSelectedTemple = useMapStore(state => state.setSelectedTemple);
  const selectedTemples = useMapStore(state => state.selectedTemples);
  const selectedRadioStation = useMapStore(state => state.selectedRadioStation);
  const selectedFuelStation = useMapStore(state => state.selectedFuelStation);
  const setSelectedFuelStation = useMapStore(state => state.setSelectedFuelStation);
  const isFuelLoading = useMapStore(state => state.isFuelLoading);
  const fuelSummary = useMapStore(state => state.fuelSummary);

  useEffect(() => {
    setIsTempleModalOpen(false);
  }, [selectedTemple]);
  const { filterHealth, resolveLandParcel } = useGisWorker();
  const setHealthFilters = useMapStore(state => state.setHealthFilters);

  const handleReport = (type: string, data: { label: string; value: string }[]) => {
    setReportModal(true, { type, data });
  };

  // Extract basic valid pincode from context if present
  const currentContextPincode = (
    searchResult?.properties?.PIN_CODE || 
    searchResult?.properties?.pincode || 
    selectedPostalOffice?.pincode ||
    selectedLocalBodyV2?.properties?.pincode ||
    selectedVeterinaryFacility?.properties?.pincode ||
    jurisdictionDetails?.pincode ||
    policeResolution?.boundary?.properties?.pincode
  )?.toString() || null;

  const commonShareUrl = currentContextPincode ? getRawShareUrl(currentContextPincode, activeLayer as ServiceLayer) : null;


  const renderStructuredData = () => {
    let officeName = '';
    let officeType = 'GovernmentOffice';
    let pin = currentContextPincode || '';
    let district = activeDistrict || '';

    if (selectedPdsShop) {
      officeName = selectedPdsShop.properties.name || '';
      district = selectedPdsShop.properties.district || district;
    } else if (selectedPoliceStation) {
      officeName = selectedPoliceStation.properties.ps_name || '';
      district = selectedPoliceStation.properties.district || district;
    } else if (selectedHealthFacility) {
      officeName = selectedHealthFacility.properties.facility_n || '';
      district = selectedHealthFacility.properties.district_n || district;
      officeType = 'Hospital';
    } else if (selectedFuelStation) {
      officeName = selectedFuelStation.properties.name || '';
      district = selectedFuelStation.properties.district || district;
      officeType = 'GasStation';
    } else if (selectedRegistrationOffice) {
      officeName = selectedRegistrationOffice.properties.sro_name || selectedRegistrationOffice.properties.office_nam || '';
      district = selectedRegistrationOffice.properties.district || district;
    } else if (jurisdictionDetails) {
      officeName = jurisdictionDetails.returned_office || jurisdictionDetails.section_na || '';
      district = jurisdictionDetails.district || district;
    } else if (searchResult) {
      const p = searchResult.properties;
      officeName = p.office_name || p.NAME || p.district || '';
      pin = (p.PIN_CODE || p.pincode || pin).toString();
    }

    if (!officeName) return null;
    
    return (
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": officeType,
          "name": officeName,
          "address": {
            "@type": "PostalAddress",
            "postalCode": pin,
            "addressRegion": district ? `${district}, Tamil Nadu` : "Tamil Nadu",
            "addressCountry": "IN"
          }
        })}
      </script>
    );
  };

  const hasResults = !!(
    (activeLayer === 'PDS' && (selectedPdsShop || searchResult)) ||
    (activeLayer === 'TNEB' && (jurisdictionDetails || searchResult)) ||
    (activeLayer === 'PINCODE' && (searchResult || selectedPostalOffice)) ||
    (activeLayer === 'CONSTITUENCY' && searchResult) ||
    (activeLayer === 'POLICE' && (policeResolution || searchResult)) ||
    (activeLayer === 'HEALTH' && (selectedHealthFacility || healthSummary || searchResult)) ||
    (activeLayer === 'LOCAL_BODIES_V2' && (isV2Loading || selectedLocalBodyV2)) ||
    (activeLayer === 'VETERINARY' && (selectedVeterinaryFacility || searchResult)) ||
    (activeLayer === 'REGISTRATION' && (selectedRegistrationOffice || searchResult)) ||
    (activeLayer === 'LAND_PARCEL' && (selectedLandParcel || isLandParcelLoading || landParcelError)) ||
    (activeLayer === 'TEMPLES' && (selectedTemple || selectedTemples || searchResult)) ||
    (activeLayer === 'RADIO' && selectedRadioStation) ||
    (activeLayer === 'FUEL' && (selectedFuelStation || fuelSummary || isFuelLoading)) ||
    noDataFound
  );

  if (!hasResults) return null;

  return (
    <div 
      className={`result-cards-stack ${isResultMinimized ? 'minimized' : ''}`} 
      aria-live="polite" 
      aria-atomic="true"
    >
      {isMobile && isResultMinimized && (
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="result-restore-btn"
          onClick={() => setIsResultMinimized(false)}
        >
          <ChevronUp size={18} style={{ color: '#f59e0b' }} />
          <span>{t('VIEW_RESULTS') || 'View Results'}</span>
        </motion.button>
      )}

      <Helmet>
        {renderStructuredData()}
      </Helmet>
      
      <AnimatePresence mode="popLayout">
        {!isResultMinimized && (
          <motion.div
            key="results-content"
            initial={isMobile ? { y: 100, opacity: 0 } : { x: 50, opacity: 0 }}
            animate={{ y: 0, x: 0, opacity: 1 }}
            exit={isMobile ? { y: 100, opacity: 0 } : { x: 50, opacity: 0 }}
            className="results-inner-container"
          >
            {/* Fuel Station Detail */}
            {activeLayer === 'FUEL' && selectedFuelStation && (() => {
              const p = selectedFuelStation.properties;
              const [lng, lat] = selectedFuelStation.geometry.coordinates as Position;
              const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
              const groups = p.fuelGroups ?? resolveFuelGroups(p.fuels);
              const phones = splitPhoneNumbers(p.contact || '');
              const amenities = (p.amenities || []).map(formatAmenity).filter(Boolean);
              const hoursText = formatHours(p.hours);
              const cardTitle = formatStationTitle(p.name, p.brand, p.city || undefined, p.address || undefined);
              const is24Seven = /24\s?hours|24\/7|24_7/i.test(hoursText) || amenities.some(a => /24\/7|24 hours/i.test(a));

              return (
                <ResultCard
                  key="fuel-detail"
                  themeColor="emerald"
                  title={cardTitle}
                  icon={<Fuel size={20} />}
                  badges={[
                    { label: getBrandLabel(p.brand), color: getBrandColor(p.brand) },
                    ...(is24Seven ? [{ label: 'Open 24/7', color: '#10b981' }] : [])
                  ]}
                  contactChips={phones.map(n => ({
                    kind: 'phone' as const, label: n.display, href: `tel:${n.dial}`
                  }))}
                  data={[
                    ...(groups.length
                      ? [{ label: '⛽ Fuels', value: groups.map(getFuelGroupLabel).join(' • '), isPill: true, isWide: true }]
                      : []),
                    ...(p.address
                      ? [{ label: '📍 Address', value: p.address, isMultiline: true }]
                      : []),
                    { label: '🗺️ District', value: p.district || 'N/A' },
                    ...(p.pincode ? [{ label: '📮 Pincode', value: p.pincode.toString() }] : []),
                    ...(hoursText ? [{ label: '🕒 Hours', value: hoursText, isMultiline: hoursText.length > 45 }] : []),
                    ...(amenities.length
                      ? [{ label: '✨ Amenities', value: amenities.join(', '), isMultiline: amenities.join(', ').length > 45 }]
                      : []),
                    ...(p.sourceUrl
                      ? [{ label: '🔗 Source', value: 'Dealer listing', href: p.sourceUrl.toString() }]
                      : [])
                  ]}
                  onClose={() => setSelectedFuelStation(null)}
                  onDirections={() => window.open(directionsUrl, '_blank')}
                  directionsUrl={directionsUrl}
                  onReport={(data) => handleReport('Fuel Station', data)}
                  onMinimize={() => setIsResultMinimized(true)}
                  featureId={`fuel_${p.id || selectedFuelStation.id}`}
                  shareLabel="Fuel Station"
                  geometry={selectedFuelStation.geometry}
                />
              );
            })()}

            {/* Fuel Area Summary */}
            {activeLayer === 'FUEL' && fuelSummary && !selectedFuelStation && !isFuelLoading && (
              <ResultCard
                key="fuel-summary"
                themeColor="emerald"
                title={`Fuel Stations in ${fuelSummary.name}`}
                icon={<Fuel size={20} />}
                data={[
                  {
                    label: t('STATUS'),
                    value: `${fuelSummary.total} ${fuelSummary.total === 1 ? 'station' : 'stations'}`,
                    isPill: true
                  },
                  ...(Object.keys(fuelSummary.countsByBrand).length
                    ? [{
                        label: '🏷️ By brand',
                        value: Object.entries(fuelSummary.countsByBrand)
                          .sort((a, b) => b[1] - a[1])
                          .map(([brand, count]) => `${getBrandLabel(brand)}: ${count}`)
                          .join(' • '),
                        isMultiline: true
                      }]
                    : []),
                  { label: t('INSTRUCTION'), value: t('CLICK_MARKER_INFO') }
                ]}
                onClose={clearSearch}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* PDS Shop Detail */}
            {activeLayer === 'PDS' && selectedPdsShop && (
              <ResultCard
                key="pds-detail"
                themeColor="red"
                title={selectedPdsShop.properties.name || t('PDS')}
                icon={<ShoppingCart size={20} />}
                data={[
                  { label: t('SHOP_CODE'), value: selectedPdsShop.properties.shop_code || 'N/A', isPill: true },
                  ...(selectedPdsShop.properties.incharge
                    ? [{ label: t('INCHARGE'), value: selectedPdsShop.properties.incharge }]
                    : []),
                  ...(selectedPdsShop.properties.contact_no
                    ? [{ label: t('CONTACT'), value: selectedPdsShop.properties.contact_no, href: `tel:${selectedPdsShop.properties.contact_no}` }]
                    : []),
                  { label: t('VILLAGE'), value: selectedPdsShop.properties.village || 'N/A' },
                  { label: t('TALUK'), value: selectedPdsShop.properties.taluk || 'N/A' },
                  { label: t('DISTRICT'), value: translateDistrict(selectedPdsShop.properties.district, language) || 'N/A' }
                ]}
                onClose={() => setSelectedPdsShop(null)}
                onDirections={() => {
                  const coords = selectedPdsShop.geometry.coordinates as Position;
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`, '_blank');
                }}
                directionsUrl={`https://www.google.com/maps/dir/?api=1&destination=${(selectedPdsShop.geometry.coordinates as Position)[1]},${(selectedPdsShop.geometry.coordinates as Position)[0]}`}
                onReport={(data) => handleReport('PDS Shop', data)}
                onMinimize={() => setIsResultMinimized(true)}
                featureId={`pds_${selectedPdsShop.properties.shop_code}`}
                shareUrl={commonShareUrl || getFeatureShareUrl(selectedPdsShop, 'PDS') || undefined}
                shareLabel={t('RATION_SHOP')}
                geometry={selectedPdsShop.geometry}
              />
            )}

            {/* PDS Layer Instruction */}
            {activeLayer === 'PDS' && searchResult && !selectedPdsShop && (
               <ResultCard
                 key="pds-instruction"
                 themeColor="red"
                 title={`PDS Shops in ${searchResult.properties.office_name || searchResult.properties.district || ''}`}
                 icon={<ShoppingCart size={20} />}
                 data={[
                   { label: t('STATUS'), value: t('DISPLAYING_LOCAL_SHOPS'), isPill: true },
                   { label: t('INSTRUCTION'), value: t('CLICK_MARKER_INFO') }
                 ]}
                 onClose={clearSearch}
                 onMinimize={() => setIsResultMinimized(true)}
               />
            )}

            {/* TNEB Section Detail */}
            {activeLayer === 'TNEB' && jurisdictionDetails && (
              <ResultCard
                key="tneb-detail"
                themeColor="orange"
                title={`${jurisdictionDetails.returned_office || jurisdictionDetails.section_na || jurisdictionDetails.section_office || t('TNEB')} (${jurisdictionDetails.section_co})`}
                icon={<Zap size={20} />}
                data={[
                  { 
                    label: t('BLOCK'), 
                    value: `${jurisdictionDetails.subdivisio || jurisdictionDetails.sub_division || 'N/A'} (${jurisdictionDetails.subdivis_1 || jurisdictionDetails.sub_div_co || '0'})`
                  },
                  { 
                    label: t('DIVISION'), 
                    value: `${jurisdictionDetails.division_n || jurisdictionDetails.division || 'N/A'} (${jurisdictionDetails.division_c || jurisdictionDetails.div_cod || '0'})`
                  },
                  { 
                    label: t('CIRCLE'), 
                    value: `${jurisdictionDetails.circle_nam || jurisdictionDetails.circle || 'N/A'} (${jurisdictionDetails.circle_cod || '0'})`
                  },
                  {
                    label: t('REGION'),
                    value: `${jurisdictionDetails.region_nam || jurisdictionDetails.region || 'N/A'} (${jurisdictionDetails.region_id || jurisdictionDetails.region_cod || '0'})`
                  },
                  ...(jurisdictionDetails.mobile
                    ? [{ label: t('MOBILE'), value: jurisdictionDetails.mobile as string, href: `tel:${jurisdictionDetails.mobile}` }]
                    : []),
                  ...(jurisdictionDetails.email
                    ? [{ label: t('EMAIL'), value: jurisdictionDetails.email as string, href: `mailto:${jurisdictionDetails.email}` }]
                    : [])
                ]}
                onClose={() => setJurisdictionDetails(null, null)}
                onDirections={jurisdictionDetails.office_location ? () => {
                  const coords = jurisdictionDetails.office_location as Position;
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`, '_blank');
                } : undefined}
                directionsUrl={jurisdictionDetails.office_location ? `https://www.google.com/maps/dir/?api=1&destination=${(jurisdictionDetails.office_location as Position)[1]},${(jurisdictionDetails.office_location as Position)[0]}` : undefined}
                onReport={(data) => handleReport('TNEB Section', data)}
                onMinimize={() => setIsResultMinimized(true)}
                featureId={`tneb_${jurisdictionDetails.section_co || jurisdictionDetails.section_office}`}
                shareUrl={commonShareUrl || undefined}
                shareLabel={t('SECTION_OFFICE')}
                geometry={jurisdictionGeometry || (jurisdictionDetails.office_location ? { type: 'Point', coordinates: jurisdictionDetails.office_location as Position } : null)}
                pointLocation={jurisdictionDetails.office_location as any}
              />
            )}

            {/* TNEB Layer Instruction */}
            {activeLayer === 'TNEB' && searchResult && !jurisdictionDetails && (
               <ResultCard
                  key="tneb-instruction"
                  themeColor="orange"
                  title={t('FIND_TNEB_SECTION')}
                  icon={<Zap size={20} />}
                  data={[
                    { label: t('AREA'), value: translateDistrict(searchResult.properties.office_name || searchResult.properties.district || 'N/A', language) },
                    { label: t('NEXT_STEP'), value: t('CLICK_FIND_TNEB') }
                  ]}
                  onClose={clearSearch}
                  onMinimize={() => setIsResultMinimized(true)}
                />
            )}

            {activeLayer === 'PINCODE' && searchResult && !selectedPostalOffice && (() => {
              const validOffices = selectedPostalOffices?.filter(o => !o.isOutlier) || [];
              const outlierOffices = selectedPostalOffices?.filter(o => o.isOutlier) || [];
              
              return (
                <ResultCard
                  key="pincode-info"
                  themeColor="blue"
                  title={searchResult.properties.office_name || searchResult.properties.district || searchResult.properties.NAME || t('CAT_PINCODE')}
                  icon={<MapPin size={20} />}
                  data={[
                    { label: t('PIN_CODE'), value: (searchResult.properties.PIN_CODE || searchResult.properties.pincode || 'N/A').toString(), isPill: true },
                    { label: t('DISTRICT'), value: translateDistrict(searchResult.properties.district, language) || 'N/A' },
                    { label: t('REGION'), value: (searchResult.properties.region_nam as string) || 'N/A' },
                    ...(validOffices.length > 0 ? [
                      { 
                        label: t('LOCAL_OFFICES'), 
                        value: `${validOffices.length} ${t('FOUND')}`
                      }
                    ] : []),
                    ...(outlierOffices.length > 0 ? [
                      {
                        label: t('POTENTIAL_DATA_ERRORS'),
                        value: `${outlierOffices.length} ${t('MISPLACED')}`
                      }
                    ] : [])
                  ]}
                  onClose={clearSearch}
                  onDirections={validOffices.length > 0 ? () => {
                     const mainOffice = validOffices.find(o => o.officetype === 'HO' || o.officetype === 'SO') || validOffices[0];
                     window.open(`https://www.google.com/maps/dir/?api=1&destination=${mainOffice.latitude},${mainOffice.longitude}`, '_blank');
                  } : undefined}
                  directionsUrl={validOffices.length > 0 ? (() => {
                     const mainOffice = validOffices.find(o => o.officetype === 'HO' || o.officetype === 'SO') || validOffices[0];
                     return `https://www.google.com/maps/dir/?api=1&destination=${mainOffice.latitude},${mainOffice.longitude}`;
                  })() : undefined}
                  onReport={(data) => handleReport('Pincode Area', data)}
                  onMinimize={() => setIsResultMinimized(true)}
                  featureId={`pincode_${searchResult.properties.PIN_CODE || searchResult.properties.pincode}`}
                  shareUrl={getFeatureShareUrl(searchResult, 'PINCODE') || undefined}
                  shareLabel={t('AREA')}
                  geometry={searchResult.geometry}
                />
              );
            })()}

            {/* Specific Post Office Detail */}
            {activeLayer === 'PINCODE' && selectedPostalOffice && (
              <ResultCard
                key="postal-detail"
                themeColor={selectedPostalOffice.isOutlier ? 'orange' : 'blue'}
                title={selectedPostalOffice.officename}
                icon={<MapPin size={20} />}
                badges={selectedPostalOffice.isOutlier ? [{ label: t('ACCURACY_WARNING'), color: '#f97316', icon: <AlertCircle size={12} /> }] : []}
                data={[
                  { label: `📍 ${t('PIN_CODE')}`, value: selectedPostalOffice.pincode, isPill: true },
                  { label: `🏢 ${t('OFFICE_TYPE')}`, value: t(getOfficeTypeLabelKey(selectedPostalOffice.officetype) as any) },
                  { label: `📦 ${t('DELIVERY_SERVICE')}`, value: !selectedPostalOffice.delivery?.toLowerCase().includes('non') ? t('YES') : t('NO') },
                  { label: `🗺️ ${t('DISTRICT')}`, value: translateDistrict(selectedPostalOffice.district, language) },
                  { label: `ℹ️ ${t('MORE_INFO')}`, value: selectedPostalOffice.divisionname, subValue: `${t('DIVISION')}: ${selectedPostalOffice.divisionname} • ${t('REGION')}: ${selectedPostalOffice.regionname}` },
                  ...(selectedPostalOffice.isOutlier ? [{ label: t('NOTE'), value: t('POSTAL_ACCURACY_DESC') }] : [])
                ]}
                onClose={() => setSelectedPostalOffice(null)}
                onDirections={!selectedPostalOffice.isOutlier ? () => {
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedPostalOffice.latitude},${selectedPostalOffice.longitude}`, '_blank');
                } : undefined}
                directionsUrl={`https://www.google.com/maps/dir/?api=1&destination=${selectedPostalOffice.latitude},${selectedPostalOffice.longitude}`}
                onReport={(data) => handleReport('Post Office', data)}
                onMinimize={() => setIsResultMinimized(true)}
                featureId={`postal_${selectedPostalOffice.pincode}_${selectedPostalOffice.officename.replace(/\s+/g, '_')}`}
                shareUrl={getRawShareUrl(selectedPostalOffice.pincode, 'PINCODE') || undefined}
                shareLabel={t('PO')}
                geometry={{
                  type: 'Point',
                  coordinates: [parseFloat(selectedPostalOffice.longitude as string), parseFloat(selectedPostalOffice.latitude as string)]
                }}
              />
            )}

            {/* Outliers List Card */}
            {activeLayer === 'PINCODE' && searchResult && !selectedPostalOffice && (() => {
              const outlierOffices = selectedPostalOffices?.filter(o => o.isOutlier) || [];
              if (outlierOffices.length === 0) return null;
              
              return (
                <div key="outliers-group" className="result-card-group">
                  <div className="result-card-group-label">
                    <AlertCircle size={14} color="#f97316" />
                    {t('MISPLACED_DATA_FOUND')}
                  </div>
                  {outlierOffices.map((off, idx) => (
                    <ResultCard
                      key={`outlier-${off.officename}-${idx}`}
                      themeColor="slate"
                      title={off.officename}
                      icon={<MapPin size={18} />}
                      data={[
                        { label: t('CATEGORY'), value: `${off.officetype} (${off.delivery})`, isPill: true },
                        { label: t('REASON'), value: t('GEOGRAPHICALLY_MISPLACED') }
                      ]}
                      actionLabel={t('VIEW_DETAILS')}
                      onAction={() => setSelectedPostalOffice(off)}
                      onClose={() => {}} 
                      onMinimize={() => setIsResultMinimized(true)}
                    />
                  ))}
                </div>
              );
            })()}

            {/* Constituency Info */}
            {activeLayer === 'CONSTITUENCY' && searchResult && (
              <div key="constituency-wrapper" style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <ResultCard
                  key="constituency-info"
                  themeColor="indigo"
                  title={(searchResult.properties.assembly_c || searchResult.properties.parliame_1 || t('CAT_CONSTITUENCY')) as string}
                  icon={<Landmark size={20} />}
                  data={searchResult.properties.assembly_c ? [
                    { label: t('AC_NUMBER'), value: searchResult.properties.assembly_1?.toString() || 'N/A', isPill: true },
                    { label: t('DISTRICT'), value: translateDistrict(searchResult.properties.district_n as string, language) || 'N/A' },
                    { label: t('CAT_CONSTITUENCY'), value: searchResult.properties.parliame_1 as string || 'N/A' },
                  ] : [
                    { label: t('PC_NUMBER'), value: searchResult.properties.parliament?.toString() || 'N/A', isPill: true },
                    { label: t('STATE'), value: t('TN') }
                  ]}
                  onClose={clearSearch}
                  onReport={(data) => handleReport('Constituency', data)}
                  onMinimize={() => setIsResultMinimized(true)}
                  featureId={`const_${searchResult.properties.assembly_1 || searchResult.properties.parliament}`}
                  shareUrl={commonShareUrl || getFeatureShareUrl(searchResult, 'CONSTITUENCY') || undefined}
                  shareLabel={t('CONSTITUENCY_SINGLE')}
                  geometry={searchResult.geometry}
                />
                
                {searchResult.properties.assembly_1 && SHOW_ELECTION_RESULTS && (
                  <ConstituencyDetailCard 
                    constituencyId={searchResult.properties.assembly_1 as number}
                    constituencyName={searchResult.properties.assembly_c as string}
                  />
                )}
              </div>
            )}

            {/* Police Station Info */}
            {activeLayer === 'POLICE' && policeResolution && (
              <ResultCard
                key="police-detail"
                themeColor="slate"
                title={policeResolution.station?.properties.ps_name || policeResolution.boundary.properties.police_sta || t('CAT_POLICE')}
                icon={<Shield size={20} />}
                data={[
                  // Trimmed to the essentials: Station, Jurisdiction, Officer (+ contact chips).
                  // Boundary status / match confidence intentionally omitted as secondary.
                  {
                    label: t('STATION_NAME'),
                    value: policeResolution.station?.properties.ps_name || policeResolution.boundary.properties.police_sta || 'N/A',
                    subValue: policeResolution.station?.properties.ps_code
                      ? `${t('STATION_CODE')}: ${policeResolution.station.properties.ps_code}`
                      : (!policeResolution.isBoundaryValid ? t('STATION_DATA_UPDATING') : undefined)
                  },
                  ...(policeResolution.isBoundaryValid ? [
                     {
                       label: t('JURISDICTION'),
                       value: `${translateDistrict(policeResolution.boundary.properties.district_n, language) || ''} ${t('DISTRICT')}`
                     }
                  ] : []),
                  // SHO officer name (contact channels render as a compact chip bar below)
                  ...(policeContact?.officer_name ? [{ label: t('STATION_OFFICER'), value: policeContact.officer_name }] : []),
                ]}
                contactChips={policeContact ? [
                  ...splitPhoneNumbers(policeContact.phone).map(n => ({
                    kind: 'phone' as const, label: n.display, href: `tel:${n.dial}`
                  })),
                  ...splitPhoneNumbers(policeContact.mobile).map(n => ({
                    kind: 'mobile' as const, label: n.display, href: `tel:${n.dial}`
                  })),
                  ...(policeContact.email ? [{
                    kind: 'email' as const,
                    label: t('EMAIL'),
                    href: `mailto:${policeContact.email.split(/[;,\s]/)[0]}`
                  }] : []),
                ] : undefined}
                onClose={() => setPoliceResolution(null)}
                onDirections={policeResolution.station ? () => {
                  const coords = (policeResolution.station!.properties.station_location || 
                                 policeResolution.station!.geometry.coordinates) as Position;
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`, '_blank');
                } : undefined}
                directionsUrl={policeResolution.station ? (() => {
                  const coords = (policeResolution.station!.properties.station_location || 
                                 policeResolution.station!.geometry.coordinates) as Position;
                  return `https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`;
                })() : undefined}
                onReport={(data) => handleReport('Police Station', data)}
                onMinimize={() => setIsResultMinimized(true)}
                featureId={`police_${policeResolution.boundary.properties.police_s_1}`}
                shareUrl={commonShareUrl || getFeatureShareUrl(policeResolution.boundary, 'POLICE') || undefined}
                shareLabel={t('POLICE_STATION')}
                geometry={policeResolution.boundary.geometry}
                pointLocation={policeResolution.station?.geometry.coordinates as any}
              />
            )}

            {activeLayer === 'LOCAL_BODIES_V2' && (isV2Loading || selectedLocalBodyV2) && (
              <div key="v2-card-wrapper" className="v2-card-wrapper" style={{ padding: '0 8px' }}>
                {isV2Loading ? (
                  <div style={{
                    background: theme === 'dark' ? '#1e293b' : '#ffffff',
                    padding: '24px',
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Activity className="animate-pulse" size={32} color="#6366f1" />
                    <span style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: theme === 'dark' ? '#94a3b8' : '#64748b' 
                    }}>
                      {t('RESOLVING_LOCAL_BODY')}
                    </span>
                  </div>
                ) : selectedLocalBodyV2 && (() => {
                  const p = selectedLocalBodyV2.properties;
                  const details = [
                    { label: t('SEARCHED_LOCATION'), value: p.searched_area, icon: MapPin },
                    { label: t('DISTRICT'), value: p.district, icon: MapPin },
                    { label: t('ZONE'), value: p.zone_name },
                    { label: t('WARD'), value: p.ward_number },
                    { label: t('TALUK'), value: p.taluk },
                    { label: t('BLOCK'), value: p.block },
                    { label: t('CATEGORY'), value: p.category },
                    { label: t('PIN_CODE'), value: p.pincode },
                  ].filter(d => d.value);

                  return (
                    <ResultCard
                      key="local-body-detail"
                      themeColor="indigo"
                      title={p.name}
                      icon={<Landmark size={20} />}
                      badges={[{ label: t(p.type as any), color: '#6366f1' }]}
                      data={details.map(d => ({ label: d.label, value: d.value as string }))}
                      onClose={() => setSelectedLocalBodyV2(null)}
                      onReport={(data) => handleReport('Local Body', data)}
                      onMinimize={() => setIsResultMinimized(true)}
                      featureId={`localbody_${p.pincode}_${p.name.replace(/\s+/g, '_')}`}
                      shareUrl={commonShareUrl || getFeatureShareUrl(selectedLocalBodyV2 as any, 'LOCAL_BODIES_V2') || undefined}
                      shareLabel={t(p.type as any) || t('LOCAL_BODIES_V2')}
                      geometry={p.ward_geometry || selectedLocalBodyV2.geometry}
                    />
                  );
                })()}
              </div>
            )}

            {/* Veterinary Facility Detail */}
            {activeLayer === 'VETERINARY' && selectedVeterinaryFacility && (() => {
              const p = selectedVeterinaryFacility.properties;
              const typeKey = `VET_${p.type || 'HOSPITAL'}` as any;
              const tierLabel = t(typeKey) || p.type || t('VETERINARY');
              const details = [
                { label: t('DISTRICT'), value: p.district_n || '' },
                { label: t('TALUK'), value: p.taluk_name || '' },
                { label: language === 'ta' ? 'பிரிவு' : 'Division', value: p.division || '' },
              ].filter(d => d.value);

              return (
                <ResultCard
                  key="veterinary-detail"
                  themeColor="indigo"
                  title={p.village_na || t('VETERINARY')}
                  icon={<Activity size={20} />}
                  badges={[{ label: tierLabel, color: '#4f46e5' }]}
                  data={details.map(d => ({ label: d.label, value: d.value as string }))}
                  onClose={() => setSelectedVeterinaryFacility(null)}
                  onReport={(data) => handleReport('Veterinary Facility', data)}
                  onMinimize={() => setIsResultMinimized(true)}
                  featureId={`vet_${p.village_na}_${p.taluk_name}`}
                  shareUrl={commonShareUrl || getFeatureShareUrl(selectedVeterinaryFacility as any, 'VETERINARY') || undefined}
                  shareLabel={t('VET_INSTITUTION' as any)}
                  geometry={selectedVeterinaryFacility.geometry}
                />
              );
            })()}

            {/* Veterinary Layer Instruction */}
            {activeLayer === 'VETERINARY' && searchResult && !selectedVeterinaryFacility && (
               <ResultCard
                 key="veterinary-instruction"
                 themeColor="indigo"
                 title={searchResult.properties.office_name || searchResult.properties.district || ''}
                 icon={<Activity size={20} />}
                 data={[
                   { label: t('STATUS'), value: 'Viewing Area Facilities', isPill: true },
                   { label: t('INSTRUCTION'), value: t('CLICK_MARKER_INFO') }
                 ]}
                 onClose={clearSearch}
                 onMinimize={() => setIsResultMinimized(true)}
               />
            )}

            {/* Police Layer Instruction */}
            {activeLayer === 'POLICE' && searchResult && !policeResolution && !noDataFound && (
                <ResultCard
                  key="police-instruction"
                  themeColor="slate"
                  title={t('FIND_POLICE_JURISDICTION')}
                  icon={<Shield size={20} />}
                  data={[
                    { label: t('AREA'), value: searchResult.properties.office_name || searchResult.properties.district || 'N/A' },
                    { label: t('NEXT_STEP'), value: t('CLICK_FIND_POLICE') }
                  ]}
                  onClose={clearSearch}
                  onMinimize={() => setIsResultMinimized(true)}
                />
             )}

            {/* Police Layer General Instruction */}
            {activeLayer === 'POLICE' && !searchResult && !policeResolution && !noDataFound && (
              <ResultCard
                key="police-general-instruction"
                themeColor="slate"
                title={t('POLICE_JURISDICTIONS')}
                icon={<Shield size={20} />}
                data={[
                  { label: t('STATUS'), value: t('STATION_FIRST_DISCOVERY'), isPill: true },
                  { label: t('INSTRUCTION'), value: t('CLICK_STATION_BOUNDARY') }
                ]}
                onClose={() => {}}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* Health Facility Detail */}
            {activeLayer === 'HEALTH' && selectedHealthFacility && (
              <ResultCard
                key="health-detail"
                themeColor="rose"
                title={selectedHealthFacility.properties.facility_n || selectedHealthFacility.properties.NAME || t('CAT_HEALTH')}
                icon={<Activity size={20} />}
                badges={[
                  ...(Number(selectedHealthFacility.properties.delivery_p) === 1 ? [{ label: t('DELIVERY_SERVICES'), color: '#ec4899' }] : []),
                  ...(String(selectedHealthFacility.properties.timing_of_ || '').includes('24x7') ? [{ label: t('EMERGENCY_24X7'), color: '#f59e0b' }] : []),
                  ...(selectedHealthFacility.properties.fru ? [{ label: t('FIRST_REFERRAL_UNIT'), color: '#be123c' }] : []),
                  ...(Number(selectedHealthFacility.properties.blood_bank) === 1 ? [{ label: t('BLOOD_BANK'), color: '#ef4444' }] : []),
                  ...(Number(selectedHealthFacility.properties.sncu) === 1 ? [{ label: t('NEWBORN_CARE'), color: '#0ea5e9' }] : []),
                  ...(Number(selectedHealthFacility.properties.dialysis_c) === 1 ? [{ label: t('DIALYSIS_CENTER'), color: '#8b5cf6' }] : [])
                ].slice(0, 4)}
                data={[
                  { 
                    label: t('CARE_LEVEL'), 
                    value: (({
                      'MCH': t('MCH'),
                      'DH': t('DH'),
                      'SDH': t('SDH'),
                      'CHC': t('CHC'),
                      'PHC': t('PHC'),
                      'HSC': t('HSC')
                    } as any)[selectedHealthFacility.properties.facility_t || '']) || selectedHealthFacility.properties.facility_t || 'N/A',
                    isPill: true 
                  },
                  { 
                    label: t('LOCATION'), 
                    value: `${selectedHealthFacility.properties.block_name || t('SUB_LOCAL_AREA')}, ${translateDistrict(selectedHealthFacility.properties.district_n || selectedHealthFacility.properties.district, language) || 'N/A'}`,
                    subValue: `${t('DISTRICT')}: ${translateDistrict(selectedHealthFacility.properties.district_n || selectedHealthFacility.properties.district, language) || 'N/A'}`
                  },
                  { 
                    label: t('TIMING'), 
                    value: String(selectedHealthFacility.properties.timing_of_ || '').includes('24x7') 
                      ? t('OPEN_24_HOURS')
                      : (String(selectedHealthFacility.properties.timing_of_ || '').toLowerCase().includes('day') ? t('DAY_SERVICES') : t('GENERAL_HOURS_CONTACT'))
                  },
                  { label: t('FACILITY_ID'), value: (selectedHealthFacility.properties.nin_number || 'N/A').toString() }
                ]}
                onClose={() => setSelectedHealthFacility(null)}
                onDirections={() => {
                  const coords = selectedHealthFacility.geometry.coordinates as [number, number];
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`, '_blank');
                }}
                directionsUrl={`https://www.google.com/maps/dir/?api=1&destination=${(selectedHealthFacility.geometry.coordinates as [number, number])[1]},${(selectedHealthFacility.geometry.coordinates as [number, number])[0]}`}
                onReport={(data) => handleReport('Health Facility', data)}
                onMinimize={() => setIsResultMinimized(true)}
                featureId={`health_${selectedHealthFacility.properties.nin_number || selectedHealthFacility.properties.facility_n}`}
                shareUrl={commonShareUrl || getFeatureShareUrl(selectedHealthFacility, 'HEALTH') || undefined}
                shareLabel={t('HEALTH_FACILITY')}
                geometry={selectedHealthFacility.geometry}
              />
            )}

            {/* Registration Office Detail */}
            {activeLayer === 'REGISTRATION' && selectedRegistrationOffice && (
              <RegistrationCard 
                onReport={(data) => handleReport('Registration Office', data)}
                shareUrl={commonShareUrl || getFeatureShareUrl(selectedRegistrationOffice, 'REGISTRATION') || undefined}
                shareLabel={t('SRO')}
              />
            )}

            {/* Land Parcel Loading */}
            {ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && isLandParcelLoading && (
               <ResultCard
                 key="land-parcel-loading"
                 themeColor="amber"
                 title={t('LAND_PARCEL')}
                 icon={<Loader2 className="animate-spin" size={20} />}
                 data={[
                   { label: t('STATUS'), value: t('UPDATING'), isPill: true },
                   { label: t('INSTRUCTION'), value: 'Fetching cadastral data from TNGIS...' }
                 ]}
                 onClose={clearSearch}
               />
            )}

            {/* Land Parcel Detail */}
            {ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && selectedLandParcel && (
              <LandParcelCard
                onReport={(data) => handleReport('Land Parcel', data)}
              />
            )}

            {/* Land Parcel Error Card */}
            {ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && landParcelError && !isLandParcelLoading && (
               <ResultCard
                 key="land-parcel-error"
                 themeColor="red"
                 title={t('LAND_PARCEL')}
                 icon={<AlertCircle size={20} />}
                 mascotPath="/branding/error-mascot.webp"
                 data={[
                   { 
                     label: t('STATUS'), 
                     value: landParcelError === 'timeout' ? t('SERVER_TIMEOUT') : t('SERVER_ERROR'), 
                     isPill: true 
                   },
                   { 
                     label: t('NOTE'), 
                     value: landParcelError === 'timeout' ? t('TNGIS_TIMEOUT_DESC') : t('TNGIS_ERROR_DESC') 
                   },
                   ...(lastClickedPoint ? [{ label: t('LOCATION'), value: `${lastClickedPoint.lat.toFixed(4)}, ${lastClickedPoint.lng.toFixed(4)}` }] : [])
                 ]}
                 onClose={() => setLandParcelError(null)}
                 actionLabel={t('RETRY') || 'Retry'}
                 onAction={() => {
                   if (lastClickedPoint) {
                     resolveLandParcel(lastClickedPoint.lat, lastClickedPoint.lng);
                   }
                 }}
               />
            )}

            {/* Land Parcel Layer Instruction */}
            {ENABLE_LAND_PARCEL && activeLayer === 'LAND_PARCEL' && !selectedLandParcel && !isLandParcelLoading && !noDataFound && !landParcelError && (
              <ResultCard
                key="land-parcel-instruction"
                themeColor="amber"
                title={t('LAND_PARCEL')}
                icon={<MapIcon size={20} />}
                data={[
                  { label: t('STATUS'), value: 'Ready', isPill: true },
                  { label: t('INSTRUCTION'), value: 'Click anywhere on the map to find Land Parcel details.' },
                  { label: t('TIP'), value: 'You can also search by entering exact coordinates in the search bar above.' }
                ]}
                onClose={() => {}}
                onMinimize={() => setIsResultMinimized(true)}
                actions={[
                  {
                    label: t('GO_TO_COORDINATES'),
                    icon: <Navigation size={16} />,
                    onClick: () => {
                      const input = prompt('Enter coordinates (latitude, longitude):', '13.0, 80.2');
                      if (input) {
                        setSearchQuery(input);
                        setUserTyping(true);
                      }
                    },
                    variant: 'secondary'
                  }
                ]}
              />
            )}

            {/* Registration Layer Instruction */}
            {activeLayer === 'REGISTRATION' && searchResult && !selectedRegistrationOffice && (
              <ResultCard
                key="registration-instruction"
                themeColor="indigo"
                title={searchResult.properties.office_name || searchResult.properties.district || ''}
                icon={<Landmark size={20} />}
                data={[
                  { label: t('STATUS'), value: t('VIEWING_SRO_AREA'), isPill: true },
                  { label: t('NEXT_STEP'), value: t('CLICK_FIND_SRO') }
                ]}
                onClose={clearSearch}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* Health Discovery Summary */}
            {activeLayer === 'HEALTH' && healthSummary && !selectedHealthFacility && (
              <HealthSummaryCard
                key="health-summary"
                summary={healthSummary}
                onClearFilters={() => {
                  const emptyFilters: HealthFilters = {
                    facilityTypes: [],
                    locationType: 'All' as const,
                    isHwc: null,
                    hasDelivery: null,
                    isFru: null,
                    is24x7: null,
                    hasBloodBank: null,
                    hasBloodStorage: null,
                    hasSncu: null,
                    hasNbsu: null,
                    hasDeic: null,
                    hasCt: null,
                    hasMri: null,
                    hasDialysis: null,
                    hasCbnaat: null,
                    hasTeleConsultation: null,
                    hasStemiHub: null,
                    hasStemiSpoke: null,
                    hasCathLab: null
                  };
                  setHealthFilters(emptyFilters);
                  const pincode = (searchResult?.properties?.PIN_CODE || searchResult?.properties?.pincode)?.toString();
                  filterHealth(healthScope, emptyFilters, activeDistrict, pincode || null);
                }}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* Health Layer Instruction (when no summary yet) */}
            {activeLayer === 'HEALTH' && !healthSummary && !selectedHealthFacility && !noDataFound && (
               <ResultCard
                 key="health-instruction"
                 themeColor="blue"
                 title={t('HEALTH_DISCOVERY')}
                 icon={<Activity size={20} />}
                 data={[
                   { label: t('STATUS'), value: t('STATEWIDE_VIEW'), isPill: true },
                   { label: t('NEXT_STEP'), value: t('SEARCH_EXPLORE_HEALTH') }
                 ]}
                 onClose={() => {}}
                 onMinimize={() => setIsResultMinimized(true)}
               />
            )}

            {/* Temples Layer Detail */}
            {activeLayer === 'TEMPLES' && selectedTemple && (() => {
              const deity = language === 'ta' ? selectedTemple.c?.mol?.tn : selectedTemple.c?.mol?.en;
              const amman = language === 'ta' ? selectedTemple.c?.amb?.tn : selectedTemple.c?.amb?.en;
              const posData = ENABLE_POS_DASHBOARD ? getPosData(selectedTemple.id) : null;
              const district = language === 'ta' ? selectedTemple.dist.tn : selectedTemple.dist.en;
              const timings = language === 'ta' ? selectedTemple.c?.hrs?.d?.tn : selectedTemple.c?.hrs?.d?.en;
              const hist = language === 'ta' ? selectedTemple.c?.hist?.tn : selectedTemple.c?.hist?.en;
              const address = language === 'ta' ? selectedTemple.c?.addr?.tn : selectedTemple.c?.addr?.en;

              // Facilities formatted list
              const facilities = language === 'ta' ? selectedTemple.c?.fac?.tn : selectedTemple.c?.fac?.en;
              let facStr = '';
              if (facilities && Array.isArray(facilities) && facilities.length > 0) {
                facStr = facilities.map((f) => `${f.title}: ${f.description}`).join('\n');
              }

              // Pujas formatted list
              const pujas = language === 'ta' ? selectedTemple.c?.pj?.tn : selectedTemple.c?.pj?.en;
              let pjStr = '';
              if (pujas && Array.isArray(pujas) && pujas.length > 0) {
                pjStr = pujas.map((p) => `${p.name} - ${p.time}`).join('\n');
              }

              // Build essential data for the ResultCard
              const essentialData = [];
              if (deity) essentialData.push({ label: language === 'ta' ? 'மூலவர்' : 'Deity', value: deity });
              if (amman) essentialData.push({ label: language === 'ta' ? 'அம்மன்' : 'Goddess', value: amman });
              if (district) essentialData.push({ label: language === 'ta' ? 'மாவட்டம்' : 'District', value: district });
              if (address) essentialData.push({ label: language === 'ta' ? 'முகவரி' : 'Address', value: address });


              // HRCE Website link
              const numericTid = selectedTemple.id.replace(/^[A-Za-z]+0*/, '');
              if (numericTid) {
                essentialData.push({
                  label: language === 'ta' ? 'இணையதளம்' : 'Website',
                  value: language === 'ta' ? 'HRCE இணையதளத்தில் காண்க' : 'VIEW WEBSITE',
                  href: `https://hrce.tn.gov.in/hrcehome/index_temple.php?tid=${numericTid}`
                });
              }

              const templeLat = parseFloat(selectedTemple.g.lat.toString());
              const templeLng = parseFloat(selectedTemple.g.lng.toString());
              const isHeritage = hist?.her === 'Yes' || hist?.her === 'Y';
              const txCount = selectedTemple.transaction_count || 0;

              // Century/Era parsing for badge
              const cen = selectedTemple.c?.hist?.en?.cen;
              let isPre10th = false;
              let centuryText = '';
              if (cen) {
                const c = String(cen).trim().toLowerCase();
                isPre10th = /before|(\b(1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)\b)/i.test(c) && 
                            !/(\b(11th|12th|13th|14th|15th|16th|17th|18th|19th|20th|21st)\b)/i.test(c);
                centuryText = cen;
              }

              // Badges
              const badges = [];

              if (isPre10th) {
                badges.push({
                  label: language === 'ta' 
                    ? `🏛️ பண்டைய தலம் (${centuryText})` 
                    : `🏛️ Ancient Relic (${centuryText})`,
                  color: '#c2410c'
                });
              }

              if (isHeritage) {
                badges.push({
                  label: language === 'ta' ? '🎖️ பாரம்பரியத் தலம்' : '🎖️ HRCE Heritage',
                  color: '#2563eb'
                });
              }

              if (txCount > 10000) {
                badges.push({
                  label: language === 'ta' ? '🔥 பிரபலமான தலம்' : '🔥 Popular Hub',
                  color: '#ea580c'
                });
              } else if (txCount === 0 && (isPre10th || isHeritage)) {
                badges.push({
                  label: language === 'ta' ? '🍃 அமைதியான தலம்' : '🍃 Peaceful Shrine',
                  color: '#16a34a'
                });
              }

              if (selectedTemple.kat === 'Y') {
                badges.push({
                  label: language === 'ta' ? 'கட்டளை தலம்' : 'Kattalai Temple',
                  color: '#10b981'
                });
              }

              if (ENABLE_POS_DASHBOARD && posData) {
                badges.push({
                  label: language === 'ta' ? 'டிஜிட்டல் கொடுப்பனவுகள்' : 'Digital Payments',
                  color: '#0d9488',
                  icon: <Coins size={12} />
                });
              }

              return (
                <>
                  <ResultCard
                    key={selectedTemple.id}
                    themeColor="purple"
                    title={language === 'ta' ? selectedTemple.nm.tn : selectedTemple.nm.en}
                    icon={<Landmark size={20} />}
                    data={essentialData}
                    badges={badges}
                    onClose={() => setSelectedTemple(null)}
                    onDirections={!isNaN(templeLat) && !isNaN(templeLng) ? () => {
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${templeLat},${templeLng}`, '_blank');
                    } : undefined}
                    directionsUrl={!isNaN(templeLat) && !isNaN(templeLng) ? `https://www.google.com/maps/dir/?api=1&destination=${templeLat},${templeLng}` : undefined}
                    onReport={(data) => handleReport('Temple Details', data)}
                    onMinimize={() => setIsResultMinimized(true)}
                    featureId={`temple_${selectedTemple.g.dc}_${selectedTemple.id}`}
                    shareUrl={getFeatureShareUrl(selectedTemple as unknown as GisFeature, 'TEMPLES') || undefined}
                    shareLabel={t('TEMPLE_FACILITY')}
                    geometry={!isNaN(templeLat) && !isNaN(templeLng) ? { type: 'Point', coordinates: [templeLng, templeLat] } : null}
                    pointLocation={!isNaN(templeLat) && !isNaN(templeLng) ? [templeLng, templeLat] : null}
                    hideAccuracy={true}
                    actions={[
                      {
                        label: language === 'ta' ? 'திசைகளைப் பெறுக' : 'Get Directions',
                        icon: <Navigation size={16} />,
                        onClick: !isNaN(templeLat) && !isNaN(templeLng) ? () => {
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${templeLat},${templeLng}`, '_blank');
                        } : () => {},
                        variant: 'primary'
                      },
                      {
                        label: language === 'ta' ? 'கூடுதல் விவரங்கள்' : 'View Full Details',
                        icon: <Landmark size={16} />,
                        onClick: () => setIsTempleModalOpen(true),
                        variant: 'secondary'
                      }
                    ]}
                  />

                  {isTempleModalOpen && (
                    <div className="temple-modal-overlay" onClick={() => setIsTempleModalOpen(false)}>
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="temple-modal-content glass"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="temple-modal-header">
                          <div className="temple-modal-title-group">
                            <Landmark className="temple-modal-icon" size={24} />
                            <div>
                              <h3 className="temple-modal-title">
                                {language === 'ta' ? selectedTemple.nm.tn : selectedTemple.nm.en}
                              </h3>
                              <span className="temple-modal-subtitle">
                                {language === 'ta' ? selectedTemple.dist.tn : selectedTemple.dist.en} {language === 'ta' ? 'மாவட்டம்' : 'District'}
                              </span>
                              {badges.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                  {badges.map((b, idx) => (
                                    <span 
                                      key={idx} 
                                      style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        gap: '4px', 
                                        padding: '3px 8px', 
                                        borderRadius: '12px', 
                                        fontSize: '10px', 
                                        fontWeight: 'bold', 
                                        color: '#ffffff',
                                        backgroundColor: b.color 
                                      }}
                                    >
                                      {b.icon}
                                      {b.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <button className="temple-modal-close" onClick={() => setIsTempleModalOpen(false)}>
                            <X size={20} />
                          </button>
                        </div>

                        <div className="temple-modal-body">
                          {/* Deities & Timings */}
                          <div className="temple-modal-section">
                            <div className="temple-modal-section-title">
                              {language === 'ta' ? 'மூலவர் & நேரம்' : 'Deities & Timings'}
                            </div>
                            <div className="temple-modal-grid">
                              {deity && (
                                <div className="temple-modal-row">
                                  <span className="temple-modal-label">{language === 'ta' ? 'மூலவர்' : 'Deity'}</span>
                                  <span className="temple-modal-value">{deity}</span>
                                </div>
                              )}
                              {amman && (
                                <div className="temple-modal-row">
                                  <span className="temple-modal-label">{language === 'ta' ? 'அம்மன்' : 'Goddess'}</span>
                                  <span className="temple-modal-value">{amman}</span>
                                </div>
                              )}
                              {timings && (
                                <div className="temple-modal-row">
                                  <span className="temple-modal-label">{language === 'ta' ? 'தரிசன நேரம்' : 'Darshan Hours'}</span>
                                  <div className="temple-modal-value-box">{timings}</div>
                                </div>
                              )}
                              {pjStr && (
                                <div className="temple-modal-row">
                                  <span className="temple-modal-label">{language === 'ta' ? 'பூஜை காலங்கள்' : 'Puja Timings'}</span>
                                  <div className="temple-modal-value-box">{pjStr}</div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* History & Architecture */}
                          {(hist?.cen || hist?.saint || hist?.tree || hist?.sha || isHeritage || selectedTemple.kat === 'Y') && (
                            <div className="temple-modal-section">
                              <div className="temple-modal-section-title">
                                {language === 'ta' ? 'வரலாறு & வடிவமைப்பு' : 'History & Architecture'}
                              </div>
                              <div className="temple-modal-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                {hist?.cen && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'நூற்றாண்டு / காலம்' : 'Century / Period'}</span>
                                    <span className="temple-modal-value">{hist.cen}</span>
                                  </div>
                                )}
                                {hist?.saint && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'பாடியவர்கள்' : 'Poet Saints'}</span>
                                    <span className="temple-modal-value">{hist.saint}</span>
                                  </div>
                                )}
                                {hist?.tree && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'தல விருட்சம்' : 'Sacred Tree'}</span>
                                    <span className="temple-modal-value">{hist.tree}</span>
                                  </div>
                                )}
                                {hist?.sha && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'கருவறை அமைப்பு' : 'Sanctorum Shape'}</span>
                                    <span className="temple-modal-value">{hist.sha}</span>
                                  </div>
                                )}
                                {isHeritage && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'வகை' : 'Status'}</span>
                                    <span className="temple-modal-value" style={{ color: '#a21caf', fontWeight: 'bold' }}>
                                      {language === 'ta' ? 'பாரம்பரிய தளம்' : 'Heritage Site'}
                                    </span>
                                  </div>
                                )}
                                {selectedTemple.kat === 'Y' && (
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'வகை' : 'Type'}</span>
                                    <span className="temple-modal-value" style={{ color: '#10b981', fontWeight: 'bold' }}>
                                      {language === 'ta' ? 'கட்டளை தளம்' : 'Kattalai Temple'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Devotee Facilities */}
                          {facStr && (
                            <div className="temple-modal-section">
                              <div className="temple-modal-section-title">
                                {language === 'ta' ? 'வசதிகள்' : 'Facilities'}
                              </div>
                              <div className="temple-modal-value-box" style={{ borderLeftColor: '#10b981' }}>{facStr}</div>
                            </div>
                          )}

                          {/* Digital Payments */}
                          {ENABLE_POS_DASHBOARD && (
                            <div className="temple-modal-section">
                              <div className="temple-modal-section-title">
                                {language === 'ta' ? 'டிஜிட்டல் கொடுப்பனவுகள்' : 'Digital Payments'}
                              </div>
                              {posData ? (
                                <div className="temple-modal-grid">
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'POS சாதனங்கள்' : 'POS Devices'}</span>
                                    <span className="temple-modal-value">{posData.device_count}</span>
                                  </div>
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'மொத்த பரிவர்த்தனைகள்' : 'Total Transactions'}</span>
                                    <span className="temple-modal-value">{posData.transaction_count.toLocaleString()}</span>
                                  </div>
                                  {posData.banks && posData.banks.length > 0 && (
                                    <div className="temple-modal-row">
                                      <span className="temple-modal-label">{language === 'ta' ? 'வங்கிகள்' : 'Banks'}</span>
                                      <span className="temple-modal-value">{posData.banks.join(', ')}</span>
                                    </div>
                                  )}
                                  <div className="temple-modal-row">
                                    <span className="temple-modal-label">{language === 'ta' ? 'கடைசியாக புதுப்பிக்கப்பட்டது' : 'Last Updated'}</span>
                                    <span className="temple-modal-value">{posData.last_updated}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="temple-modal-value-box" style={{ borderLeftColor: '#ef4444' }}>
                                  {language === 'ta' ? 'இந்த கோயிலுடன் தற்போது POS சாதனங்கள் இணைக்கப்படவில்லை.' : 'No POS devices currently linked to this temple.'}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Location & Address */}
                          <div className="temple-modal-section">
                            <div className="temple-modal-section-title">
                              {language === 'ta' ? 'முகவரி' : 'Address'}
                            </div>
                            <div className="temple-modal-grid">
                              {address && (
                                <div className="temple-modal-row">
                                  <span className="temple-modal-value">{address}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Temples Layer Instruction / Pincode list overview */}
            {activeLayer === 'TEMPLES' && searchResult && !selectedTemple && (
              <ResultCard
                key="temple-instruction"
                themeColor="purple"
                title={language === 'ta' ? `கோயில்கள் (HRCE) - ${searchResult.properties.office_name || searchResult.properties.district || ''}` : `Temples (HRCE) in ${searchResult.properties.office_name || searchResult.properties.district || ''}`}
                icon={<Landmark size={20} />}
                data={[
                  { label: t('PIN_CODE'), value: (searchResult.properties.PIN_CODE || searchResult.properties.pincode || 'N/A').toString(), isPill: true },
                  { 
                    label: language === 'ta' ? 'கோயில்கள் (HRCE) எண்ணிக்கை' : 'Temples (HRCE) Count', 
                    value: selectedTemples ? `${selectedTemples.length} ${t('FOUND')}` : `0 ${t('FOUND')}`
                  },
                  { label: t('INSTRUCTION'), value: language === 'ta' ? 'கோவில் விவரங்களைப் பார்க்க வரைபடத்தில் உள்ள மார்க்கர்களை அழுத்தவும்.' : 'Click markers on the map to view temple details.' }
                ]}
                onClose={clearSearch}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* No Data Found Card - Suppress if a primary entity panel is already showing */}
            {noDataFound && !selectedVeterinaryFacility && !selectedLocalBodyV2 && !selectedHealthFacility && !selectedPdsShop && !jurisdictionDetails && !selectedLandParcel && !landParcelError && !selectedTemple && (
              <ResultCard
                key="no-data"
                themeColor="blue"
                title={t('NO_INFO_FOUND')}
                icon={<AlertCircle size={20} />}
                mascotPath="/branding/confused-mascot.webp"
                data={[
                  { label: t('STATUS'), value: t('DATA_UNAVAILABLE'), isPill: true },
                  { label: t('NOTE'), value: t('NO_JURISDICTION_DATA') },
                  { label: t('LOCATION'), value: lastClickedPoint ? `${lastClickedPoint.lat.toFixed(4)}, ${lastClickedPoint.lng.toFixed(4)}` : 'Unknown' }
                ]}
                onClose={() => setNoDataFound(false)}
                actionLabel={t('CONTRIBUTE_DATA')}
                onAction={(data) => handleReport('Missing Data', data)}
                onDirections={lastClickedPoint ? () => {
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${lastClickedPoint.lat},${lastClickedPoint.lng}`, '_blank');
                } : undefined}
                onReport={(data) => handleReport('Missing Data', data)}
                onMinimize={() => setIsResultMinimized(true)}
              />
            )}

            {/* Radio Station Group Card */}
            {activeLayer === 'RADIO' && selectedRadioStation && (
              <RadioStationCard key="radio-station-group" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResultContainer;
